import { AfterViewInit, Component, ViewChild, signal } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { CatalogService, CatalogProductItem, CatalogSearchRequest, CatalogSearchResponse } from '../../../core/services/catalog/catalog.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ROLE_VTEX_CATALOG_READ } from '../../../core/models/user/user-info.model';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { TabsComponent } from '../../../shared/components/tabs/tabs.component';
import { TabPaneComponent } from '../../../shared/components/tabs/tab-pane.component';
import { TradePolicyModel } from '../../../core/models/catalog/trade-policy.model';
import { PaginationState } from '../../../core/models/common/pageable.model';
import { FeatherPipe } from '../../../core/pipes/feather.pipe';

@Component({
  selector: 'app-admin-product-search',
  imports: [
    CommonModule,
    FormsModule,
    AlertComponent,
    LoadingComponent,
    ButtonComponent,
    NgOptimizedImage,
    ModalComponent,
    TabsComponent,
    TabPaneComponent,
    PaginationComponent,
    RouterLink,
    FeatherPipe,
  ],
  templateUrl: './admin-product-search.component.html',
  styleUrl: './admin-product-search.component.scss',
})
export class AdminProductSearchComponent implements AfterViewInit {
  readonly searchType = signal<'ProductId' | 'RefId' | 'SkuId' | 'ProductName'>('RefId');
  readonly searchValue = signal('');
  readonly order = signal<'ASC' | 'DESC'>('DESC');
  readonly size = signal(25);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly total = signal(0);
  readonly data = signal<CatalogProductItem[]>([]);
  readonly pageIndex = signal(0);
  readonly startIndex = signal(0);
  readonly endIndex = signal(0);

  readonly paginationState = signal<PaginationState>({
    currentPage: 0,
    currentPageItems: 0,
    itemsPerPage: 25,
    totalItems: 0,
    loading: false,
  });

  private lastAfter?: { after: number; afterDate: string };
  private lastBefore?: { before: number; beforeDate: string };

  @ViewChild('inventoryModal') inventoryModalRef!: ModalComponent;
  readonly modalTitle = signal('');
  readonly modalProduct = signal<CatalogProductItem | null>(null);
  readonly version = signal<'v1' | 'v2'>('v1');
  readonly tradePolicies = signal<TradePolicyModel[]>([]);

  constructor(
    private readonly catalog: CatalogService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly auth: AuthenticationService,
  ) {}

  ngAfterViewInit(): void {
    const isMinimal = !this.auth.hasRole(ROLE_VTEX_CATALOG_READ);
    this.version.set(isMinimal ? 'v2' : 'v1');
    this.catalog.getTradePolicies().subscribe({
      next: (tp) => this.tradePolicies.set(tp),
    });
  }

  private buildRequest(forward?: boolean, backward?: boolean): CatalogSearchRequest {
    const req: CatalogSearchRequest = {
      size: this.size(),
      order: this.order(),
      orderBy: 'Date',
    };
    const v = (this.searchValue() ?? '').trim();
    if (v) {
      const t = this.searchType();
      if (t === 'ProductId') req.productId = Number(v);
      if (t === 'RefId') req.refId = v;
      if (t === 'SkuId') req.skuId = Number(v);
      if (t === 'ProductName') req.productName = v;
    }
    if (forward && this.lastAfter) {
      req.after = this.lastAfter.after;
      req.afterDate = this.lastAfter.afterDate;
    }
    if (backward && this.lastBefore) {
      req.before = this.lastBefore.before;
      req.beforeDate = this.lastBefore.beforeDate;
    }
    return req;
  }

  search(): void {
    this.error.set(null);
    this.pageIndex.set(0);
    this.lastAfter = undefined;
    this.lastBefore = undefined;

    const v = (this.searchValue() ?? '').trim();
    const t = this.searchType();
    if ((t === 'ProductId' || t === 'SkuId') && v) {
      const n = Number(v);
      if (!Number.isInteger(n) || n <= 0) {
        this.error.set('Valor numérico inválido.');
        return;
      }
    }

    this.loading.set(true);
    this.paginationState.update((p) => ({ ...p, loading: true }));

    this.catalog.search(this.buildRequest()).subscribe({
      next: (res: CatalogSearchResponse) => {
        this.applyResponse(res);
        this.loading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.data.set([]);
        this.total.set(0);
        this.startIndex.set(0);
        this.endIndex.set(0);
        this.paginationState.set({
          currentPage: 0,
          currentPageItems: 0,
          itemsPerPage: this.size(),
          totalItems: 0,
          loading: false,
        });
        this.error.set('Falha ao realizar a busca.');
        this.loading.set(false);
      },
    });
  }

  readonly onPageChange = (page: number, pageSize: number): void => {
    const current = this.pageIndex();
    if (page < current) {
      this.prevPage();
    } else if (page > current) {
      this.nextPage();
    } else if (pageSize !== this.size()) {
      this.size.set(pageSize);
      this.search();
    }
  };

  nextPage(): void {
    const items = this.data();
    if (!items?.length) return;

    const last = items[items.length - 1];
    this.lastAfter = { after: last.id, afterDate: last.lastUpdateDate };
    this.lastBefore = undefined;

    this.loading.set(true);
    this.paginationState.update((p) => ({ ...p, loading: true }));

    this.catalog.search(this.buildRequest(true, false)).subscribe({
      next: (res) => {
        this.pageIndex.update((i) => i + 1);
        this.applyResponse(res);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Falha ao carregar a próxima página.');
        this.loading.set(false);
        this.paginationState.update((p) => ({ ...p, loading: false }));
      },
    });
  }

  prevPage(): void {
    const items = this.data();
    if (!items?.length) return;

    const first = items[0];
    this.lastBefore = { before: first.id, beforeDate: first.lastUpdateDate };
    this.lastAfter = undefined;

    this.loading.set(true);
    this.paginationState.update((p) => ({ ...p, loading: true }));

    this.catalog.search(this.buildRequest(false, true)).subscribe({
      next: (res) => {
        this.pageIndex.update((i) => Math.max(0, i - 1));
        this.applyResponse(res);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Falha ao carregar a página anterior.');
        this.loading.set(false);
        this.paginationState.update((p) => ({ ...p, loading: false }));
      },
    });
  }

  private applyResponse(res: CatalogSearchResponse): void {
    const items = res?.data ?? [];
    const total = res?.total ?? 0;
    const idx = this.pageIndex();
    const sz = this.size();

    this.data.set(items);
    this.total.set(total);
    if (items.length) {
      this.startIndex.set(idx * sz + 1);
      this.endIndex.set(idx * sz + items.length);
    } else {
      this.startIndex.set(0);
      this.endIndex.set(0);
    }
    this.paginationState.set({
      currentPage: idx,
      currentPageItems: items.length,
      itemsPerPage: sz,
      totalItems: total,
      loading: false,
    });
  }

  editProduct(productId: number): void {
    this.router.navigate(['/catalog/product', productId]);
  }

  openModal(product: CatalogProductItem): void {
    this.modalProduct.set(product);
    this.modalTitle.set(product.name);
    this.inventoryModalRef.openDialog();
  }

  getSalesChannelString(salesChannels: number | string[] | string | undefined): string {
    const policies = this.tradePolicies();
    if (typeof salesChannels === 'number') {
      return policies.find((x) => x.id === salesChannels)?.name ?? String(salesChannels);
    }
    if (typeof salesChannels === 'string') {
      return policies.find((x) => x.id === Number(salesChannels))?.name ?? String(salesChannels);
    }
    if (Array.isArray(salesChannels)) {
      return salesChannels.join(', ');
    }
    return 'Não possui';
  }
}
